import React, { useCallback } from 'react';
import { motion } from 'framer-motion';
import { useQueryClient } from '@tanstack/react-query';
import { useProjectsStateContext } from '@/components/providers/ProjectsStateProvider/projects-state-provider';
import { MotionLink } from '@/components/ui/Motion';
import { Skeleton } from '@/components/ui/skeleton';
import { cls, formatForUrl } from '@/lib/utils';
import { getImageUrlsByFolder } from '@/services/cloudinary';
import BackgroundImage from '@/components/ui/layout/background-image';
import type { ProjectName } from '../../types';

const MAX_RESULTS = 10;

const cardMotionProps = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.4 },
};

interface ProjectsSectionItemProperties {
  image: string;
  projectName: ProjectName;
  index: number;
}

function ProjectsSectionItem({
  image,
  projectName,
  index,
}: ProjectsSectionItemProperties) {
  const { imageUrlErrors } = useProjectsStateContext();
  const queryClient = useQueryClient();

  const handleOnMouseEnter = useCallback(() => {
    const folder = `projects/${projectName}`;
    const tags = '';
    const queryKey = ['get_image_urls_by_folder', folder, MAX_RESULTS, tags];

    const isCached = queryClient.getQueryData(queryKey);

    if (isCached) {
      return;
    }

    queryClient
      .prefetchQuery({
        queryFn: () =>
          getImageUrlsByFolder({ folder, maxResults: MAX_RESULTS, tags }),
        queryKey,
      })
      .catch((error: unknown) => error);
  }, [queryClient, projectName]);

  const hasImage = imageUrlErrors?.[index] === undefined && image;

  return (
    <motion.div {...cardMotionProps}>
      <MotionLink
        href={`/projects/${formatForUrl(projectName)}`}
        onMouseEnter={handleOnMouseEnter}
        className={cls`block aspect-4/3 relative rounded-lg overflow-hidden`}
      >
        {hasImage ? (
          <BackgroundImage
            src={image}
            alt={`${projectName} custom home project`}
            fill
            sizes="(max-width: 640px) 100vw, 50vw"
            overlay="bg-black/30 hover:bg-black/50 transition-all duration-300"
            foreground="flex items-end justify-start p-4"
            className="absolute inset-0"
            imageProps={{ showSkeleton: true }}
          >
            <span className={cls`text-2xl md:text-3xl font-prata text-white`}>
              {projectName}
            </span>
          </BackgroundImage>
        ) : (
          <Skeleton className={cls`w-full h-full`} />
        )}
      </MotionLink>
    </motion.div>
  );
}

export default ProjectsSectionItem;
